// import {
//   TextureLoader,
//   WebGLRenderer,
//   PerspectiveCamera,
//   Scene,
//   PlaneBufferGeometry,
//   Mesh,
//   MeshBasicMaterial,
// } from 'three';
import gsap from 'gsap';
import loop from 'raf-loop';
import events from 'events-mixin';
// import round from 'lodash.round';

import { isMobile } from '@/scripts/utils';

class Faces {
  constructor(options) {
    this.isEntered = false;
    this.$canvas = document.querySelector('#canvas');
    this.callback = options.callback;

    this.canvasH = this.$canvas.clientHeight;
    this.canvasW = this.$canvas.clientWidth;
    this.windowHalfX = window.innerWidth / 2;
    this.windowHalfY = window.innerHeight / 2;
    this.ctx = null;
    this.isMobile = isMobile();

    // Load all the options
    this.columns = options.cols || 4;
    this.elements = options.elements || 1;
    this.perspective = options.perspective || 500;
    this.urlImg = options.urlImg;
    this.bgTexture = null;
    // Update to handle mouse movment scale
    this.mouseXScale = options.mouseXScale || 0.005;
    this.mouseYScale = options.mouseYScale || 0.008;
    this.mouseZScale = options.mouseZScale || 0.001;
    this.deviceMoveScaleX = options.deviceMoveScaleX || 4;
    this.deviceMoveScaleY = options.deviceMoveScaleY || 7;
    this.facesIteration = options.facesIteration + 1 || 4;
    this.sceneScale = options.sceneScale || 1;
    if( this.isMobile ) this.sceneScale = options.mobileSceneScale || 1;

    // Internal properties for calculation
    this.mouseY = 0;
    this.mouseX = 0;
    this.isStarting = true;
    this.orientation = {
      x: 0,
      y: 0,
    };
    this.parameters = [];
    this.alphas = [];
    this.zs = [];
    this.meshes = [];
    this.scales = {
      initial: 0,
      final: 0,
    };
    this.three = null;

    import(/* webpackChunkName: "three" */ 'three').then((three) => {
      this.three = three;

      this.textureLoader = new this.three.TextureLoader();
      this.init();
    });
  }

  init() {
    const ratio = this.$canvas.clientWidth / this.$canvas.clientHeight;
    const FOV = (2 * Math.atan(window.innerHeight / (2 * this.perspective))) * (180 / Math.PI);
    this.camera = new this.three.PerspectiveCamera(FOV, ratio, 0.1, 5000);
    this.camera.position.z = this.perspective;

    // SCENE
    this.scene = new this.three.Scene();

    this.textureLoader.load( "/static/img/faces/faceoftheworld_bg.png", bgTexture => {
      this.bgTexture = bgTexture;
      this.textureLoader.load(this.urlImg, (texture) => {
        this.loadMeshes(texture, this.facesIteration).then(() => {
          this.engine = loop(this.animate.bind(this));
          this.play();
          this.onEnter();
        });
      });
    } );


    // RENDERER
    this.renderer = new this.three.WebGLRenderer({ alpha: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.canvasW, this.canvasH);
    this.ctx = this.renderer.domElement;

    this.$canvas.appendChild(this.ctx);
    this.ctx.style.pointerEvents = 'none';

    this.callback();

    this.bindEvents();
  }

  bindEvents() {
    this.onResize = this.onWindowResize.bind(this);
    window.addEventListener('resize', this.onResize);

    this.events = events(window, this);
    const desktopEvents = {
      mousemove: 'onDocumentMouseMove',
      // touchstart: 'onDocumentTouchStart',
      // touchmove: 'onDocumentTouchMove',
    };

    window.addEventListener('deviceorientation', this.onOrientation.bind(this));
    // const deviceEvent = this.isMobile ? mobileEvents : desktopEvents;
    const deviceEvent = desktopEvents;
    // EVENTS
    if (!this.isMobile) this.events.bind(deviceEvent);
  }

  onOrientation(event) {
    if (this.isStarting) {
      this.isStarting = false;
      this.orientation = {
        x: Math.round(event.gamma),
        y: Math.round(event.beta),
      };
    }

    const xValue = (this.orientation.x - Math.round(event.gamma)) * this.deviceMoveScaleX;
    const yValue = (this.orientation.y - Math.round(event.beta)) * this.deviceMoveScaleY;

    this.mouseX = xValue;
    this.mouseY = -yValue;
  }

  loadMeshes(texture, amount) {
    return new Promise((resolve) => {
      const size = 200;
      const baseDistance = this.isMobile ? -300 : 0;
      const step = size / amount;

      this.naturalSizes = {
        w: texture.image.naturalWidth,
        h: texture.image.naturalHeight,
      };

      this.scales.initial = (this.canvasH / this.naturalSizes.h) * this.sceneScale;
      this.scales.final = this.scales.initial;
      this.realSizes = {
        w: this.naturalSizes.w * this.scales.final,
        h: this.naturalSizes.h * this.scales.final,
      };

      for (let i = 0; i < amount; i += 1) {
        // const revertedIndex = Math.abs(i - textures.length);
        const geo = new this.three.PlaneBufferGeometry(
          texture.image.naturalWidth,
          texture.image.naturalHeight);

        // const alphaDelta = (0.8 - 0.3) / amount;
        const alphaDelta = 0.9 / amount;
        const alpha = 0.9 - (alphaDelta * i);
        const z = Math.round((step * (i + 1)) - (step * (amount / 2)))
        + baseDistance;

        const materialOpts = {
          map: i != 0 ? texture : this.bgTexture,
          transparent: true
        };
        const material = new this.three.MeshBasicMaterial(materialOpts);
        const mesh = new this.three.Mesh(geo, material);

        this.alphas.unshift( i != 0 ? alpha : 1 );
        this.zs.unshift(z);
        this.parameters.unshift({
          x: 0,
          y: 0,
          alpha: 0,
          z,
        });

        this.scene.add(mesh);

        resolve();
      }
    });
  }

  onEnter() {
    const tl = new gsap.TimelineMax({
      onComplete: () => {
        setTimeout(() => {
          this.isEntered = true;
        }, 500);
      },
    });

    let count = -1;
    tl.staggerTo(this.parameters, 0.5, {
      alpha: () => {
        count += 1;
        return this.alphas[count];
      },
    }, 0.1);
  }

  onWindowResize() {
    const SCREEN_WIDTH = this.$canvas.clientWidth;
    const SCREEN_HEIGHT = this.$canvas.clientHeight;
    this.renderer.setSize(SCREEN_WIDTH, SCREEN_HEIGHT);
    this.camera.aspect = SCREEN_WIDTH / SCREEN_HEIGHT;
    this.camera.updateProjectionMatrix();
  }

  onDeviceRotation(e) {
    this.mouseX = 0;
    this.mouseY = 0;
  }

  onDocumentMouseMove(e) {
    this.mouseX = e.clientX - this.windowHalfX;
    this.mouseY = e.clientY - this.windowHalfY;
  }

  onDocumentTouchStart(e) {
    if (e.touches.length === 1) {
      e.preventDefault();
      this.mouseX = e.touches[0].pageX - this.windowHalfX;
      this.mouseY = e.touches[0].pageY - this.windowHalfY;
    }
  }

  onDocumentTouchMove(e) {
    if (e.touches.length === 1) {
      e.preventDefault();
      this.mouseX = e.touches[0].pageX - this.windowHalfX;
      this.mouseY = e.touches[0].pageY - this.windowHalfY;
    }
  }

  destroy() {
    if (this.isEntered) {
      this.stop();
      this.engine = null;

      window.removeEventListener('resize', this.onResize);
      this.events.unbind();

      this.renderer.dispose();
      delete this.renderer;
      this.ctx.remove();
    }
  }

  stop() {
    if (this.isEntered) this.engine.stop();
  }

  play() {
    this.engine.start();
  }

  animate() {
    const deltaY = ((this.mouseY / 4) - this.camera.position.y) * this.mouseYScale;
    const deltaX = ((this.mouseX) + this.camera.position.x) * this.mouseXScale;
    const normMouseX = this.mouseX / (window.innerWidth / 2);
    const deltaZ = (((Math.cos(Math.PI * (normMouseX)) * 200) + 1000)
      - this.camera.position.z) * this.mouseZScale;
    // console.log('deltaZ: ', deltaZ, ' ', this.camera.position.z);

    // if (this.isEntered) {
    this.camera.position.x -= deltaX;
    this.camera.position.y += deltaY;
    // this.camera.position.z += deltaZ;

    // console.log('this.camera.position.x : ', this.camera.rotation.x);
    const rotationY = (this.mouseX / ((window.innerWidth / 2) / 0.05)) * this.mouseYScale;
    const rotationX = (this.mouseY / ((window.innerWidth / 2) / 5)) * this.mouseXScale;

    this.scene.children.forEach((el, i) => {
      const element = el;
      // element.position.set(this.parameters[i].x, this.parameters[i].y, this.parameters[i].z);
      element.position.set(
        this.parameters[i].x -= (deltaX / 10),
        this.parameters[i].y += (deltaY / 4),
        this.parameters[i].z,
      );
      element.scale.set(this.scales.final, this.scales.final, this.scales.final);
      element.material.opacity = this.parameters[i].alpha;
      element.rotation.set(rotationX, rotationY, 0);
    });

    this.camera.lookAt(this.scene.position);
    this.renderer.render(this.scene, this.camera);
  }
}

export default Faces;



// WEBPACK FOOTER //
// ./src/scripts/faces.js